import { useEffect, useMemo, useState } from 'react'
import { BookOpen, Edit3, Play, RefreshCw, Search } from 'lucide-react'
import StoryEditor from '../pages/StoryEditor'
import { Card } from '../../../components/ui/Card'
import { useI18n } from '../../../utils/i18n'

export default function StoryPanel({ wsConnected, wsRef, targetDeviceId, deviceOnline, searchParams, setSearchParams }) {
  const { tx } = useI18n()
  const tab = useMemo(() => {
    const raw = String(searchParams?.get?.('tab') || '').toLowerCase()
    return raw === 'editor' ? 'editor' : 'play'
  }, [searchParams])

  const [loading, setLoading] = useState(false)
  const [stories, setStories] = useState([])
  const [query, setQuery] = useState('')
  const [lastError, setLastError] = useState('')

  const filtered = useMemo(() => {
    const q = String(query || '').trim().toLowerCase()
    const items = Array.isArray(stories) ? stories : []
    if (!q) return items
    return items.filter((s) => {
      const id = String(s?.id || '').toLowerCase()
      const title = String(s?.title || '').toLowerCase()
      return id.includes(q) || title.includes(q)
    })
  }, [stories, query])

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams || undefined)
    if (next === 'editor') params.set('tab', 'editor')
    else params.delete('tab')
    setSearchParams(params, { replace: true })
  }

  const loadStories = async () => {
    setLoading(true)
    setLastError('')
    try {
      const resp = await fetch('/api/story_editor', { cache: 'no-store' })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || data.error) {
        setLastError(String(data.error || resp.status))
        setStories([])
        return
      }
      setStories(Array.isArray(data.stories) ? data.stories : [])
    } catch (e) {
      setLastError(String(e))
      setStories([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (tab !== 'play') return
    loadStories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const canPlay = wsConnected && deviceOnline && String(targetDeviceId || '').trim()

  const sendPlay = (storyId) => {
    const id = String(storyId || '').trim()
    if (!id) return
    if (!wsConnected) return
    if (!wsRef?.current || wsRef.current.readyState !== WebSocket.OPEN) return
    const target = String(targetDeviceId || '').trim()
    wsRef.current.send(JSON.stringify({ type: 'play_story', story_id: id, target_device_id: target }))
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-slate-900 font-extrabold">{tx({ zh: '故事', en: 'Stories' })}</div>
            <div className="text-slate-500 text-xs font-medium">
              {tx({
                zh: '用于给设备下发故事播放；编辑故事会保存到云端故事库。',
                en: 'Send stories to the device for playback; edits are saved to the cloud story library.',
              })}
            </div>
          </div>

          <div className="flex rounded-2xl overflow-hidden border-2 border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={() => setTab('play')}
              className={`px-4 py-2 text-xs transition-colors font-bold flex items-center gap-2 ${
                tab === 'play' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              title={tx({ zh: '发送/播放', en: 'Send/Play' })}
            >
              <BookOpen className="w-4 h-4" />
              {tx({ zh: '发送', en: 'Send' })}
            </button>
            <button
              type="button"
              onClick={() => setTab('editor')}
              className={`px-4 py-2 text-xs transition-colors font-bold flex items-center gap-2 ${
                tab === 'editor' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              title={tx({ zh: '编辑器', en: 'Editor' })}
            >
              <Edit3 className="w-4 h-4" />
              {tx({ zh: '编辑', en: 'Edit' })}
            </button>
          </div>
        </div>
      </Card>

      {tab === 'editor' ? (
        <StoryEditor embedded />
      ) : (
        <Card className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="text-sm font-extrabold text-slate-900">{tx({ zh: '故事列表', en: 'Story list' })}</div>
              <div className="text-xs text-slate-500 font-medium">
                {loading ? tx({ zh: '加载中…', en: 'Loading…' }) : tx({ zh: '共 {n} 条', en: '{n} total' }, { n: filtered.length })}
                {lastError ? tx({ zh: '（错误：{e}）', en: ' (error: {e})' }, { e: lastError }) : ''}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-2xl border-2 border-slate-100 bg-white">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={tx({ zh: '搜索 id / 标题', en: 'Search id / title' })}
                  className="w-[220px] max-w-full outline-none text-sm text-slate-700 placeholder:text-slate-400"
                />
              </div>
              <button
                type="button"
                onClick={loadStories}
                className="px-4 py-2 rounded-2xl border-2 border-slate-100 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {tx({ zh: '刷新', en: 'Refresh' })}
              </button>
            </div>
          </div>

          {!wsConnected && (
            <div className="mt-4 rounded-2xl border-2 border-amber-100 bg-amber-50 text-amber-800 text-sm font-bold px-4 py-3">
              {tx({
                zh: '控制台未连接云端，无法下发故事（请先在页面上方点“重连”）。',
                en: 'Console not connected to cloud; cannot send stories (click “Reconnect” at the top first).',
              })}
            </div>
          )}

          {wsConnected && !deviceOnline && (
            <div className="mt-4 rounded-2xl border-2 border-amber-100 bg-amber-50 text-amber-800 text-sm font-bold px-4 py-3">
              {tx({
                zh: '设备离线，当前只能浏览故事；设备上线后可点击“播放到设备”。',
                en: 'Device offline: you can browse stories only. When online, click “Play to device”.',
              })}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((s) => (
              <div
                key={s.id}
                className="p-4 rounded-2xl border-2 border-slate-100 bg-white hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-slate-900 font-extrabold truncate">{s.title || s.id}</div>
                    <div className="text-slate-500 text-xs font-mono truncate">{s.id}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => sendPlay(s.id)}
                    disabled={!canPlay}
                    className="px-3 py-2 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white transition-colors font-bold flex items-center gap-2"
                    title={
                      canPlay
                        ? tx({ zh: '播放到设备', en: 'Play to device' })
                        : tx({ zh: '需要：控制台已连接 + 设备在线 + 填写 device-id', en: 'Requires: connected console + device online + device-id filled' })
                    }
                  >
                    <Play className="w-4 h-4" />
                    {tx({ zh: '播放', en: 'Play' })}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
